import { gpCommon } from '../app';
import { ISimpleHttpService } from './authorized-http-service';
import {
    IStorageProvider,
    IStorageProviderFactory,
    StorageFileInfo,
    StorageProviderFactoryServiceName,
} from './storage-provider-contract';

const STORAGE_API = '/api/gameplan/storage';

class BlobStorageProvider implements IStorageProvider {

    constructor(
        private readonly $q: ng.IQService,
        private readonly $http: ISimpleHttpService,
        private readonly scopeId: string,
    ) { }

    private encodePath(path: string): string {
        return encodeURIComponent(this.scopeId + '/' + path);
    }

    read(path: string): ng.IPromise<ArrayBuffer> {
        const url = STORAGE_API + '/files?path=' + this.encodePath(path);
        return this.$http.get<ArrayBuffer>(url, { responseType: 'arraybuffer' })
            .then((response: any) => {
                return response.data as ArrayBuffer;
            });
    }

    write(path: string, data: ArrayBuffer | Blob, contentType?: string): ng.IPromise<void> {
        const url = STORAGE_API + '/files?path=' + this.encodePath(path);
        const ct = contentType || 'application/octet-stream';

        return this.$http.put(url, data, {
            transformRequest: (d: any) => d,
            headers: { 'Content-Type': ct },
        }).then(() => undefined);
    }

    list(prefix?: string): ng.IPromise<StorageFileInfo[]> {
        let url = STORAGE_API + '/list?scope=' + encodeURIComponent(this.scopeId);
        if (prefix) {
            url += '&prefix=' + encodeURIComponent(prefix);
        }
        return this.$http.get<StorageFileInfo[]>(url).then((response: any) => {
            const data = response.data || response;
            return (data as any[]).map(item => ({
                path: item.path,
                sizeBytes: item.sizeBytes || 0,
                contentType: item.contentType || 'application/octet-stream',
                lastModified: item.lastModified || '',
            }));
        });
    }

    delete(path: string): ng.IPromise<void> {
        const url = STORAGE_API + '/files?path=' + this.encodePath(path);
        return this.$http.delete(url).then(() => undefined);
    }

    exists(path: string): ng.IPromise<boolean> {
        const url = STORAGE_API + '/exists?path=' + this.encodePath(path);
        return this.$http.get<{ exists: boolean }>(url).then((response: any) => {
            const data = response.data || response;
            return !!data.exists;
        });
    }
}

class BlobStorageProviderFactory implements IStorageProviderFactory {

    static $inject = ['$q', '$httpAuth'];
    static serviceName = StorageProviderFactoryServiceName;

    private readonly providers = new Map<string, BlobStorageProvider>();

    constructor(
        private readonly $q: ng.IQService,
        private readonly $http: ISimpleHttpService,
    ) { }

    getProvider(scopeId: string): IStorageProvider {
        let provider = this.providers.get(scopeId);
        if (!provider) {
            provider = new BlobStorageProvider(this.$q, this.$http, scopeId);
            this.providers.set(scopeId, provider);
        }
        return provider;
    }
}

gpCommon.service(BlobStorageProviderFactory.serviceName, BlobStorageProviderFactory);
